import React from 'react';
import { addError, addAction } from '@shared/utils/logger';
import { isChunkLoadError } from '@shared/utils/isChunkLoadError';
import { ChunkErrorFallback } from './ChunkErrorFallback';

type ChunkErrorBoundaryState = Readonly<{
  hasError: boolean;
  error: Error | null;
}>;

export type ChunkErrorBoundaryProps = Readonly<{
  /** Identifies the section in NR telemetry (e.g. "guest/widget/registryList") */
  section: string;
  /** Optional custom fallback. Defaults to a translated "couldn't load" message. */
  fallback?: React.ReactNode;
}>;

/**
 * Section-level error boundary for lazy-loaded guest site components.
 *
 * Catches chunk load failures (and other render errors) within a single
 * layout section or widget — preventing one broken section from taking
 * down the entire page. Reports to New Relic with the section name for
 * faceted analysis.
 *
 * Unlike the top-level ErrorBoundary in App.tsx, this boundary:
 * - Uses joykit-free inline styles (no dependency on the design system,
 *   which may itself be the chunk that failed)
 * - Only affects the wrapped section, not the full page
 * - Includes a section-specific reload that cache-busts via ?retry=N
 * - Renders inside the guest app providers, so its default fallback copy is
 *   translated (see {@link ChunkErrorFallback}). Callers outside an i18n
 *   provider must pass their own `fallback`.
 */
export class ChunkErrorBoundary extends React.Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const chunkError = isChunkLoadError(error);

    addError(error, {
      componentStack: errorInfo.componentStack,
      boundary: 'ChunkErrorBoundary',
      section: this.props.section,
      isChunkLoadError: chunkError,
    });

    addAction('ChunkErrorBoundaryCatch', {
      section: this.props.section,
      errorName: error.name,
      errorMessage: (error.message ?? '').slice(0, 200),
      isChunkLoadError: chunkError,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }

  private handleReload = () => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    // Only cache-bust for chunk failures — a `?retry=N` bump does nothing for a
    // genuine render error and just mutates the URL on every reload. Mirrors the
    // top-level ErrorBoundary's gating.
    if (isChunkLoadError(this.state.error)) {
      const current = Number(url.searchParams.get('retry')) || 0;
      url.searchParams.set('retry', String(current + 1));
    }
    window.location.assign(url.toString());
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    return (
      <ChunkErrorFallback
        isChunkLoadError={isChunkLoadError(this.state.error)}
        onReload={this.handleReload}
      />
    );
  }
}
